"use client"

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react"
import { Button } from "@cloudflare/kumo/components/button"
import { Notice } from "@/components/kumo/feedback"

import {
  TOAST_DURATION_MS,
  toastReducer,
  type Toast,
  type ToastAction,
  type ToastKind,
} from "@/modules/shell/toast-store"

import styles from "./toast-provider.module.css"

type ToastInput = {
  title: string
  subtitle?: string
  action?: ToastAction
}

type ToastApi = {
  loading: (title: string, subtitle?: string) => string
  success: (title: string, subtitle?: string, action?: ToastAction) => string
  warning: (title: string, subtitle?: string, action?: ToastAction) => string
  error: (title: string, subtitle?: string, action?: ToastAction) => string
  info: (title: string, subtitle?: string, action?: ToastAction) => string
  update: (id: string, patch: Partial<Omit<Toast, "id">>) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

let nextToastSequence = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, dispatch] = useReducer(toastReducer, [])
  const timersRef = useRef<Map<string, number>>(new Map())
  const timerSignaturesRef = useRef<Map<string, string>>(new Map())

  const api = useMemo<ToastApi>(() => {
    const push = (kind: ToastKind, input: ToastInput) => {
      const id = createToastId()
      dispatch({
        type: "push",
        toast: {
          id,
          kind,
          title: kind === "loading" ? withEllipsis(input.title) : input.title,
          subtitle: input.subtitle,
          action: input.action,
        },
      })
      return id
    }

    return {
      loading: (title, subtitle) => push("loading", { title, subtitle }),
      success: (title, subtitle, action) => push("success", { title, subtitle, action }),
      warning: (title, subtitle, action) => push("warning", { title, subtitle, action }),
      error: (title, subtitle, action) => push("error", { title, subtitle, action }),
      info: (title, subtitle, action) => push("info", { title, subtitle, action }),
      update: (id, patch) => dispatch({ type: "update", id, patch }),
      dismiss: (id) => dispatch({ type: "dismiss", id }),
    }
  }, [])

  useEffect(() => {
    const activeIds = new Set(toasts.map((toast) => toast.id))
    for (const [id, timer] of timersRef.current) {
      if (activeIds.has(id)) continue
      window.clearTimeout(timer)
      timersRef.current.delete(id)
      timerSignaturesRef.current.delete(id)
    }

    for (const toast of toasts) {
      const duration = durationForToast(toast)
      const signature = timerSignature(toast, duration)
      if (timerSignaturesRef.current.get(toast.id) === signature) continue

      const existing = timersRef.current.get(toast.id)
      if (existing) window.clearTimeout(existing)
      timersRef.current.delete(toast.id)
      timerSignaturesRef.current.set(toast.id, signature)

      if (duration === null) continue
      const timer = window.setTimeout(() => {
        dispatch({ type: "dismiss", id: toast.id })
      }, duration)
      timersRef.current.set(toast.id, timer)
    }
  }, [toasts])

  useEffect(() => {
    return () => {
      for (const timer of timersRef.current.values()) {
        window.clearTimeout(timer)
      }
      timersRef.current.clear()
      timerSignaturesRef.current.clear()
    }
  }, [])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0 ? (
        <div className={styles.toastViewport} aria-label="Notifications">
          {toasts.map((toast) => (
            <ToastItem
              key={toast.id}
              toast={toast}
              onDismiss={() => dispatch({ type: "dismiss", id: toast.id })}
            />
          ))}
        </div>
      ) : null}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error("useToast must be used inside ToastProvider.")
  return api
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  return (
    <div className={styles.toastItem}>
      <Notice
        tone={toast.kind === "loading" ? "info" : toast.kind}
        title={toast.title}
        description={toast.subtitle}
        onCloseButtonClick={onDismiss}
        action={
          toast.action ? (
            <Button onClick={toast.action.onClick}>{toast.action.label}</Button>
          ) : undefined
        }
      />
    </div>
  )
}

function createToastId(): string {
  nextToastSequence += 1
  return `toast-${Date.now().toString(36)}-${nextToastSequence.toString(36)}`
}

function withEllipsis(title: string): string {
  const trimmed = title.trim()
  if (trimmed.endsWith("…") || trimmed.endsWith("...")) return trimmed
  return `${trimmed}…`
}

function durationForToast(toast: Toast): number | null {
  if (toast.action) return null
  return TOAST_DURATION_MS[toast.kind]
}

function timerSignature(toast: Toast, duration: number | null): string {
  return [
    toast.kind,
    toast.title,
    toast.subtitle ?? "",
    toast.action?.label ?? "",
    duration ?? "sticky",
  ].join("\u0000")
}
